import { useToast } from '@/components/ui/toast'
import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
import { useLocaleStore } from '@/store/localStore'
import { useUnifiedChatStore } from '@/store/app/unifiedChat'
import type { AIChatSession } from '@/types/generated/types.gen'

const RENAME_FAILED_MESSAGES: Record<string, string> = {
  en: 'Could not rename this chat',
  de: 'Chat konnte nicht umbenannt werden',
  fr: 'Impossible de renommer ce chat'
}

export interface UseChatRenameOptions {
  /**
   * Hook invoked after a successful server commit. Typical callers plug in their
   * history-popover ref's ``refreshSessions`` so the renamed title bypasses the
   * popover fetch cooldown and shows up on the next open.
   */
  onAfterCommit?: () => void | Promise<void>
}

export interface RenameOutcome {
  status: 'noop' | 'committed' | 'failed'
  session: AIChatSession | null
}

/**
 * Shared chat-session rename pipeline used by every surface that exposes a rename
 * affordance (fullscreen inline rename, fullscreen dialog, side-panel dialog).
 *
 * Encapsulates:
 *
 * - No-op guard for empty / unchanged titles.
 * - Optimistic update across every unified-store slot that holds the session
 *   (side panel + fullscreen) via ``refreshActiveSessionIfMatching``.
 * - Mirror of the optimistic / committed / rolled-back payloads into
 *   ``globalChatStore.chatSessions`` so the sidebar and the fullscreen history
 *   popover fallback list stay in sync.
 * - Server PATCH via ``unifiedChatStore.updateSession``.
 * - Destructive toast plus automatic rollback on failure.
 *
 * :param options: See :class:`UseChatRenameOptions`.
 *
 * :return: ``renameSession`` which resolves with a :class:`RenameOutcome`
 *     describing how the rename ended (``noop`` / ``committed`` / ``failed``).
 *     Failures are surfaced via toast and the promise resolves rather than
 *     rejects so call sites don't need to double-handle the error UX.
 */
export function useChatRename(options: UseChatRenameOptions = {}) {
  const unifiedChatStore = useUnifiedChatStore()
  const globalChatStore = useGlobalChatStore()
  const localeStore = useLocaleStore()
  const { toast } = useToast()

  /**
   * Resolve the "rename failed" label against the active locale. The composable
   * uses a plain dictionary + ``useLocaleStore`` instead of ``useTranslation``
   * (``useI18n({ messages })``) so it doesn't clash with the caller's own local
   * i18n scope — vue-i18n forbids two local-scope ``useI18n`` calls in the same
   * component setup.
   */
  const renameFailedLabel = () =>
    RENAME_FAILED_MESSAGES[localeStore.getLocale] ?? RENAME_FAILED_MESSAGES.en

  /**
   * Persist a new session title, optimistically updating every surface first and
   * rolling back on failure.
   *
   * Error contract: this function **always resolves** and never rejects. Failures
   * are surfaced via a destructive toast and reflected in the returned
   * :class:`RenameOutcome` (``status: 'failed'``). Callers do **not** need their
   * own try/catch. The underlying ``unifiedChatStore.updateSession`` sets
   * ``sessionError`` on failure; we explicitly clear it on rollback so consumers
   * reading ``sessionError`` do not see a stale "Failed to update chat session"
   * banner after the rollback has already restored the previous title.
   *
   * :param session: The session to rename (must have ``uuid``).
   * :param newTitle: The raw new title; trimmed before comparison and persist.
   * :return: :class:`RenameOutcome` with ``status`` and the resulting session.
   */
  const renameSession = async (
    session: AIChatSession,
    newTitle: string
  ): Promise<RenameOutcome> => {
    if (!session.uuid) return { status: 'noop', session: null }
    const trimmed = newTitle.trim()
    const oldTitle = session.title ?? ''
    if (!trimmed || trimmed === oldTitle) return { status: 'noop', session: null }

    const optimistic: AIChatSession = { ...session, title: trimmed }
    unifiedChatStore.refreshActiveSessionIfMatching(session.uuid, optimistic)
    globalChatStore.updateChatSession(optimistic)

    try {
      const updated = await unifiedChatStore.updateSession(session.uuid, { title: trimmed })
      // ``updateSession`` already refreshes matching unified-store slots; we still
      // mirror the server payload into the global store so the sidebar / popover
      // list sees the canonical title (and not just the optimistic one).
      globalChatStore.updateChatSession(updated)
      await options.onAfterCommit?.()
      return { status: 'committed', session: updated }
    } catch (error) {
      const rollback: AIChatSession = { ...session, title: oldTitle }
      unifiedChatStore.refreshActiveSessionIfMatching(session.uuid, rollback)
      globalChatStore.updateChatSession(rollback)
      // ``unifiedChatStore.updateSession`` has already set ``sessionError`` to
      // "Failed to update chat session"; we own the user-facing toast here so
      // clear the store-level error to prevent consumers from rendering a stale
      // banner once the rollback has finished.
      unifiedChatStore.clearSessionError()
      toast({ title: renameFailedLabel(), variant: 'destructive' })
      if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
        console.debug('[useChatRename] rename failed', error)
      }
      return { status: 'failed', session: rollback }
    }
  }

  return { renameSession }
}
